// "New project" creation picker, extracted from pages/studios.js as Stage 1 of
// the studios barrel-split (#11). It must stay EAGER: the home page's
// EmptyProjectCard / ProjectStarter open it via `content: { extends:
// 'NewProjectPickerBody' }` (bridged through workspace/components/index.js), and
// home never loads the studios page chunk.
//
// Fully portable — the create flow is SDK + cache + router, no board engine. The
// list-cache helpers come from ./studioShared.js so this module never imports
// pages/studios.js (that edge would re-pin the board engine eager, defeating
// Stage 2).
// Bare shared-library import, not per-file deep functions/*.js subpaths — a
// per-file deep specifier into @symbo-ls/workspace-shared cannot resolve
// through the platform's public importmap (tickets/execution-board.md §1,
// same fix as workspace/app.js — NewProjectPicker rides the EAGER chunk per
// the header note above, so this is a reached path). Deferred call wrappers,
// not a module-scope destructure, per the a5d0a2aa barrel-cycle lesson.
import _wsShared from '@symbo-ls/workspace-shared'
const canvasGetSDK = (...args) => _wsShared.functions.getSDK(...args)
import {
  FEATURED_TEMPLATES,
  replaceGeneratedProjectData
} from '../functions/newProjectTemplates.js'
// Per-file delegate names (frank flat globalScope: studios.js and
// studioShared.js read the same shared functions under their own names; a
// components-file EXPORT of them was a `dropped-export`).
const _npBuildProjectUrls = (...args) => _wsShared.functions.buildProjectUrls(...args)
const _npProjectHref = (...args) => _wsShared.functions.projectHref(...args)
import { orgIdForSlug, prependProjectToCache } from './studioShared.js'

// ─── Featured template definitions ──────────────────────────────────────────
// Each entry defines page stubs passed as `pages` content when creating a
// project from a template. `templateProjectKey` — when set, the server
// deep-clones pages/components/designSystem from that project's ProjectData
// instead of using the inline `pages` stub. Format: "owner/key".
// ─── newProjectPicker submit ──────────────────────────────────────────────────
// Dismissal is owned by the @symbo.ls/windows layer: header Close / backdrop /
// ESC route through the ModalRoot delegate; explicit buttons call
// el.call('closeModal', 'newProjectPicker').
async function submitNewProjectPicker(root, rootEl) {
  const p = root?.newProjectPicker
  if (!p || p.creating) return
  const name = (p.name || '').trim()
  if (!name) {
    root.update({
      newProjectPicker: { ...p, error: 'canvas.newProject.error.nameRequired' }
    })
    return
  }

  // Determine template source:
  //  - tab === 'library' → user picked a specific project key to scaffold from
  //  - tab === 'featured' → use FEATURED_TEMPLATES entry (may have templateProjectKey)
  const tab = p.tab || 'featured'
  const tpl = FEATURED_TEMPLATES.find((t) => t.id === p.templateId) || FEATURED_TEMPLATES[0]

  // Library tab requires a project selection before submitting.
  if (tab === 'library' && !p.templateProject) {
    root.update({
      newProjectPicker: { ...p, error: 'canvas.newProject.error.pickSource' }
    })
    return
  }

  const slugRaw =
    String(name)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-+|-+$)/g, '')
      .slice(0, 40) || `project-${Math.random().toString(36).slice(2, 6)}`

  root.update({ newProjectPicker: { ...p, creating: true, error: null } })

  const sdk = canvasGetSDK()
  if (!sdk?.createProject && !sdk?.createOrgProject) {
    root.update({
      newProjectPicker: { ...p, creating: false, error: 'canvas.newProject.error.noSdk' }
    })
    return
  }

  // Resolve org id from slug. `orgIdForSlug` walks the localStorage caches AND
  // falls back to `sdk.listOrganizations()` if neither is populated.
  const slug = p.ownerSlug || root.userSlug || root.activeWorkspace || root.slug || ''
  const isPersonalOwner = !!root.userSlug && slug === root.userSlug
  let orgId = null
  if (!isPersonalOwner) {
    try {
      orgId = await orgIdForSlug(slug)
    } catch (orgErr) {
      console.warn('[submitNewProjectPicker] orgIdForSlug failed:', orgErr?.message || orgErr)
    }
  }
  if (!isPersonalOwner && !orgId) {
    root.update({
      newProjectPicker: {
        ...p,
        creating: false,
        error: 'canvas.newProject.error.noOrg'
      }
    })
    return
  }

  try {
    // Resolve projectType. Order:
    //  1. Library tab → always 'library' (server schema enum)
    //  2. Labs-navbar quick-create intent (p.projectType) wins when set
    //  3. Featured-template type (tpl.projectType — when user picked a card)
    //  4. Default to 'app'
    // Never send 'project' — it is NOT in the allowed list and the server
    // rejects after a long validation round-trip.
    const payload = {
      name,
      projectType: tab === 'library' ? 'library' : p.projectType || tpl.projectType || 'website',
      slug: slugRaw
    }

    // Determine the templateProject key to send for server-side deep-clone.
    if (tab === 'library' && p.templateProject) {
      payload.templateProject = p.templateProject
    } else if (tpl.templateProjectKey) {
      payload.templateProject = tpl.templateProjectKey
      if (tpl.pages) payload.pages = tpl.pages
    } else if (tpl.pages) {
      payload.pages = tpl.pages
    }

    if (tpl.components) payload.components = tpl.components
    if (tpl.canvas) payload.canvas = tpl.canvas

    const project = isPersonalOwner
      ? await sdk.createProject(payload)
      : await sdk.createOrgProject(orgId, payload)

    if (tpl.id === 'blank') {
      const projectId = project?._id || project?.id
      if (!projectId) throw new Error('Created project is missing an id')
      await replaceGeneratedProjectData(sdk, projectId, tpl)
    }
    // Optimistically insert into the shared projects cache so the workspace
    // board and dashboard sidebar reflect the new project without a reload.
    prependProjectToCache(project)
    // Pop the windows layer — its onClose clears the carrier state.
    rootEl?.call?.('closeModal', 'newProjectPicker')
    const owner = slug || project?.owner?.slug
    const key = project?.key || slugRaw
    const urls = _npBuildProjectUrls(owner, key)
    // Open the fresh project on the workspace-scoped form when it was created
    // inside the active org; personal/cross-org creates keep the legacy path.
    if (urls) rootEl.router(_npProjectHref(root, urls.safeOwner, urls.safeKey), rootEl.getRoot())
  } catch (err) {
    root.update({
      newProjectPicker: {
        ...p,
        creating: false,
        error: err?.message || 'Create failed.'
      }
    })
  }
}

// ─── NpTemplateCard — child card for the featured-templates grid ──────────────
// Referenced by childExtends: 'NpTemplateCard' in NewProjectPickerBody. Prefixed
// "Np" to avoid key-based auto-extend collisions with shared components. Matches
// the home page's AppCard chrome so the picker reads as part of the same surface
// family. Selected state lights up the background (`overlay`).
export const NpTemplateCard = {
  tag: 'button',
  type: 'button',
  subscribeTo: ['newProjectPicker'],
  flow: 'y',
  gap: 'Z',
  round: 'radiusControl',
  padding: 'B',
  textAlign: 'left',
  cursor: 'pointer',
  color: 'title',
  // Selection is monochrome (DESIGN_LANGUAGE.md §1/§4.3) — the old 'blue.1'
  // accent fill marked selection with hue.
  background: (el, s) =>
    (s.root?.newProjectPicker?.templateId || 'blank') === s._tplId ? 'selected' : 'transparent',
  'aria-pressed': (el, s) =>
    (s.root?.newProjectPicker?.templateId || 'blank') === s._tplId ? 'true' : 'false',
  // No transition on background — it's a reactive factory (subscribeTo
  // re-runs it on every newProjectPicker update), and the runtime re-applies
  // background per tick on factory-driven props, restarting a CSS transition
  // on it every time (framework constraint, DESIGN_LANGUAGE.md §7.7 / §11.3).
  ':hover': { background: 'hover' },

  // Bare design-system icon (no tile wrapper, default size) — inherits the
  // card's neutral `color: 'title'` via the Icon's `fill: currentColor`.
  Icon: {
    icon: (el, s) => s._tplIcon || 'star'
  },

  NpCardLabel: {
    tag: 'span',
    text: (el, s) => (s._tplLabel ? el.call('polyglot', s._tplLabel) : ''),
    fontSize: 'A',
    fontWeight: '700',
    color: 'title'
  },
  NpCardHint: {
    tag: 'p',
    text: (el, s) => (s._tplHint ? el.call('polyglot', s._tplHint) : ''),
    margin: '0',
    fontSize: 'Y',
    lineHeight: '1.4',
    color: 'caption'
  },
  onClick: (e, el, s) => {
    e.stopPropagation()
    const p = s.root?.newProjectPicker || {}
    s.root?.update({
      newProjectPicker: {
        ...p,
        templateId: s._tplId,
        projectType: s._tplProjectType || 'website'
      }
    })
  }
}

// ─── NewProjectPickerBody ────────────────────────────────────────────────────
// "New project" creation — body-only content for the @symbo.ls/windows custom
// layer (id 'newProjectPicker'). Triggered by the StudioNavbar create menu,
// home's EmptyProjectCard / ProjectStarter and the studios empty-board tutorial.
// State key: root.newProjectPicker = { open, tab, templateId, templateProject,
// name, creating, error }.
export const NewProjectPickerBody = {
  // No custom card chrome — the @symbo.ls/windows ModalWindow (CustomTemplate)
  // owns the surface (background / radius / shadow / width via the openModal
  // `width` option), exactly like AppCreateOrgModal.
  flow: 'column',
  maxHeight: 'min(90vh, 680px)',
  overflow: 'hidden',

  // Brand window header — its Close icon (data-key="Close") is dismissed by the
  // ModalRoot's document delegate. Populate H + P so the brand's placeholder
  // slots don't show leftover 'Heading / Paragraph' text.
  ModalHeader: {
    padding: 'A B',
    Hgroup: {
      H: { text: '{{ canvas.newProject.title | polyglot }}', fontWeight: '700' },
      P: { text: '{{ canvas.newProject.subtitle | polyglot }}' }
    }
  },

  // Project name row — uses brand InputField (Title label + themed Input).
  NpNameRow: {
    padding: 'V B',
    paddingTop: 'X',

    NpName: {
      extends: 'InputField',
      Title: {
        text: '{{ canvas.newProject.nameLabel | polyglot }}',
        fontSize: 'Z',
        fontWeight: '700',
        textTransform: 'uppercase',
        letterSpacing: '0.06em'
      },
      Input: {
        type: 'text',
        placeholder: (el) => el.call('polyglot', 'canvas.newProject.namePlaceholder'),
        onRender: (el, s) => {
          if (el.__npFocused) return
          el.__npFocused = true
          setTimeout(() => el.node?.focus?.(), 60)
        },
        onInput: (e, el, s) => {
          const p = s.root?.newProjectPicker || {}
          s.root?.update({
            newProjectPicker: { ...p, name: e.target.value, error: null }
          })
        },
        onKeydown: (e, el, s) => {
          // ESC dismiss is owned by the ModalRoot's document-level handler — an
          // explicit closeModal here would double-pop when modals stack.
          if (e.key === 'Enter') submitNewProjectPicker(s.root, el.getRoot())
        }
      }
    }
  },

  // Featured templates grid — 3 columns spanning the panel body.
  NpFeaturedGrid: {
    display: 'grid',
    style: { gridTemplateColumns: 'repeat(3, 1fr)' },
    gap: 'V',
    padding: 'A B',
    overflow: 'auto',
    children: () =>
      FEATURED_TEMPLATES.map((tpl) => ({
        _tplId: tpl.id,
        _tplLabel: tpl.label,
        _tplHint: tpl.hint,
        _tplIcon: tpl.icon,
        _tplProjectType: tpl.projectType
      })),
    childrenAs: 'state',
    childExtends: 'NpTemplateCard'
  },

  NpFooter: {
    padding: 'V B',
    // Was `borderTop: 'hairline solid line'` — divider-as-chrome. The house
    // ModalFooter primitive (company/packages/brand/components/ModalFooter.js)
    // separates by spacing alone (no border); this bespoke footer now matches.
    flow: 'y',
    gap: 'V',

    // Error row — full-width above the actions. Long server messages wrap
    // cleanly and the Create button stays the same shape regardless of length.
    NpError: {
      tag: 'p',
      hide: (el, s) => !s.root?.newProjectPicker?.error,
      // Key-in-state — a raw SDK message passes through polyglot unchanged.
      text: (el, s) =>
        s.root?.newProjectPicker?.error ? el.call('polyglot', s.root.newProjectPicker.error) : '',
      fontSize: 'Y2',
      lineHeight: '1.4',
      color: 'red',
      margin: '0',
      wordBreak: 'break-word'
    },
    // THE APP SPLIT (docs/APP_SPLIT.md): this modal makes things you PUBLISH.
    // A tool for the team is a workspace app — it has its own flow on /apps.
    NpToWorkspaceApps: {
      extends: 'Link',
      href: '/apps',
      alignSelf: 'flex-start',
      fontSize: 'Z2',
      color: 'caption',
      textDecoration: 'underline',
      ':hover': { color: 'title' },
      ':active': { color: 'title' },
      text: '{{ canvas.newProject.toWorkspaceApps | polyglot }}',
      onClick: (e, el) => {
        e.preventDefault()
        e.stopPropagation()
        el.call('closeModal', 'newProjectPicker')
        el.call('openAppPage', '/apps')
      }
    },
    NpActions: {
      flow: 'row',
      gap: 'Z1',
      align: 'center flex-end',
      // Brand Buttons — the same secondary/primary pair the tickets modal uses.
      NpCancel: {
        extends: 'Button',
        theme: 'secondary',
        type: 'button',
        minHeight: 'controlH',
        padding: '0 A',
        round: 'radiusPill',
        fontSize: 'Z2',
        text: '{{ canvas.newProject.cancel | polyglot }}',
        onClick: (e, el, s) => {
          e.stopPropagation()
          el.call('closeModal', 'newProjectPicker')
        }
      },
      NpCreate: {
        extends: 'Button',
        theme: 'primary',
        type: 'button',
        minHeight: 'controlH',
        padding: '0 A',
        round: 'radiusPill',
        fontSize: 'Z2',
        fontWeight: '700',
        disabled: (el, s) => !!s.root?.newProjectPicker?.creating,
        text: (el, s) =>
          el.call(
            'polyglot',
            s.root?.newProjectPicker?.creating
              ? 'canvas.newProject.creating'
              : 'canvas.newProject.create'
          ),
        onClick: (e, el, s) => {
          e.stopPropagation()
          submitNewProjectPicker(s.root, el.getRoot())
        }
      }
    }
  }
}
